import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Settings, AlertTriangle, TrendingDown, Info } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

function ConfigurationArticleModal({ 
  isOpen, 
  onClose, 
  article,
  onSuccess
}) {
  const [seuilAlerte, setSeuilAlerte] = useState(article?.seuil_alerte != null ? String(article.seuil_alerte) : '');
  const [consommationJour, setConsommationJour] = useState(article?.consommation_jour != null ? String(article.consommation_jour) : (article?.consommation_quotidienne != null ? String(article.consommation_quotidienne) : ''));
  const [unite, setUnite] = useState(article?.unite || 'pièces');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (isOpen && article) {
      setSeuilAlerte(article.seuil_alerte != null ? String(article.seuil_alerte) : '');
      setConsommationJour(article.consommation_jour != null ? String(article.consommation_jour) : (article.consommation_quotidienne != null ? String(article.consommation_quotidienne) : ''));
      setUnite(article.unite || 'pièces');
    }
  }, [isOpen, article]);

  const parseNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return NaN;
    const normalized = typeof value === 'string' ? value.replace(',', '.') : value;
    const result = parseFloat(normalized);
    return Number.isFinite(result) ? result : NaN;
  };

  const getParsedValues = () => {
    const parsedSeuil = parseNumericValue(seuilAlerte);
    const parsedConso = parseNumericValue(consommationJour);
    return { parsedSeuil, parsedConso };
  };

  const { parsedSeuil, parsedConso } = getParsedValues();
  const seuilEstInvalide = seuilAlerte !== '' && (!Number.isFinite(parsedSeuil) || parsedSeuil < 0);
  const consommationEstInvalide = consommationJour !== '' && (!Number.isFinite(parsedConso) || parsedConso <= 0);

  const calculerJoursRestants = () => {
    if (!Number.isFinite(parsedConso) || parsedConso <= 0) return '∞';
    const stockActuel = Number.isFinite(article?.stock) ? article.stock : 0;
    const jours = Math.floor(stockActuel / parsedConso);
    if (jours < 1) return '< 1 jour';
    if (jours === 1) return '1 jour';
    if (jours < 7) return `${jours} jours`;
    const semaines = Math.floor(jours / 7);
    return `${semaines} semaine${semaines > 1 ? 's' : ''} (${jours}j)`;
  };

  const handleSave = async () => {
    const { parsedSeuil, parsedConso } = getParsedValues();
    if (!article?.id) {
      toast.error('Article invalide');
      return;
    }
    if (!Number.isFinite(parsedSeuil) || parsedSeuil < 0) {
      toast.error('Veuillez saisir un seuil d\'alerte valide');
      return;
    }
    if (!Number.isFinite(parsedConso) || parsedConso <= 0) {
      toast.error('Veuillez saisir une consommation journalière valide');
      return;
    }

    setLoading(true);
    try {
      await axios.put(`/items/${article.id}`, {
        seuil_alerte: parsedSeuil,
        consommation_jour: parsedConso,
        unite: unite
      });
      
      toast.success('✅ Configuration mise à jour');
      onSuccess && onSuccess();
      onClose();
    } catch (error) {
      console.error('Erreur mise à jour config:', error.response?.data || error.message || error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="bg-gradient-to-r from-orange-50 to-red-50">
          <CardTitle className="flex items-center space-x-2">
            <Settings className="w-5 h-5 text-orange-600" />
            <span>Configuration Article</span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 pt-4">
          <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
            <p className="text-sm font-medium text-blue-900">
              📦 {article?.nom || 'Article'}
            </p>
            <p className="text-xs text-blue-700 mt-1">
              Stock actuel: {article?.stock || 0} {article?.unite || 'pièces'}
            </p>
          </div>

          <div className="space-y-2">
            <Label className="flex items-center space-x-2">
              <AlertTriangle size={14} className="text-red-600" />
              <span>Seuil d'alerte</span>
              <span className="flex items-center text-xs text-gray-500">
                <Info className="w-4 h-4 mr-1 text-blue-500" />
                Définissez le niveau qui déclenchera l'alerte de stock. Utilisez 0,3 pour 0,3 {unite}.
              </span>
            </Label>
            <Input
              type="text"
              value={seuilAlerte}
              onChange={(e) => setSeuilAlerte(e.target.value)}
              placeholder="Ex: 0,3"
              className={seuilEstInvalide ? 'border-red-500 shadow-sm focus:border-red-500 focus:ring-red-500' : ''}
            />
            <p className="text-xs text-gray-500">
              Vous serez alerté quand le stock passe sous ce seuil.
            </p>
            {seuilEstInvalide && (
              <p className="text-xs text-red-600">Veuillez saisir un seuil d'alerte valide (ex: 0,3).</p>
            )}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label className="flex items-center space-x-2">
                <TrendingDown size={14} className="text-purple-600" />
                <span>Consommation/jour</span>
              </Label>
              <Input
                type="text"
                value={consommationJour}
                onChange={(e) => setConsommationJour(e.target.value)}
                placeholder="Ex: 0,1"
                className={consommationEstInvalide ? 'border-red-500 shadow-sm focus:border-red-500 focus:ring-red-500' : ''}
              />
              {consommationEstInvalide && (
                <p className="text-xs text-red-600">Veuillez saisir une consommation journalière valide (supérieure à 0).</p>
              )}
            </div>
            <div className="space-y-2">
              <Label>Unité</Label>
              <Select value={unite} onValueChange={setUnite}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pièces">Pièce(s)</SelectItem>
                  <SelectItem value="g">Grammes (g)</SelectItem>
                  <SelectItem value="kg">Kilogrammes (kg)</SelectItem>
                  <SelectItem value="mL">Millilitres (mL)</SelectItem>
                  <SelectItem value="L">Litres (L)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {Number.isFinite(parsedConso) && parsedConso > 0 && article?.stock > 0 && (
            <div className="bg-green-50 p-3 rounded-lg border border-green-200">
              <p className="text-sm text-green-800">
                ⏱️ <strong>Durée restante:</strong> {calculerJoursRestants()}
              </p>
              <p className="text-xs text-green-700 mt-1">
                Avec {consommationJour} {unite}/jour, votre stock actuel durera {calculerJoursRestants()}
              </p>
            </div>
          )}

          {Number.isFinite(parsedSeuil) && parsedSeuil > 0 && article?.stock <= parsedSeuil && (
            <div className="bg-red-50 p-3 rounded-lg border border-red-200">
              <p className="text-sm text-red-800">
                ⚠️ <strong>Alerte activée !</strong>
              </p>
              <p className="text-xs text-red-700 mt-1">
                Le stock actuel ({article.stock}) est en dessous du seuil ({parsedSeuil})
              </p>
            </div>
          )}

          <div className="flex space-x-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={loading}
              className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
            >
              {loading ? 'Enregistrement...' : '✓ Enregistrer'}
            </Button>
            <Button
              onClick={onClose}
              variant="outline"
              className="flex-1"
              disabled={loading}
            >
              Annuler
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default ConfigurationArticleModal;
